import { globals } from 'config/globals';
import { particles } from 'app/contracts/Particles';
import { geometry } from 'app/contracts/Geometry';
import { Inertia } from 'utils/motion/Inertia';

export class ParticleProperties implements particles.ParticleProperties {
	public setPosition: boolean = true;

	public scaleFactorM: number = 0;

	public _x: Inertia;
	public _y: Inertia;
	public targetScaleSaved: number;
	private _rotation: Inertia;
	private _scale: Inertia;
	private _breathing: any = {
		active: false,
		config: null,
		addValue: 0,
		direction: 1
	};
	private _alpha: Inertia;
	private _easeMemory: particles.ParticleEaseConfig;


	constructor(private _sprite: PIXI.Sprite) {
		this._x = new Inertia(8);
		this._y = new Inertia(8);
		this._rotation = new Inertia(1);
		this._scale = new Inertia(5);
		this._alpha = new Inertia(3);
	}

	public get x(): number {
		return this._x.value;
	}
	public set x(value: number) {
		this._x.value = value;
	}

	public setTargetX(value: number) {
		this._x.target = value;
	}
	public increaseTargetX(value: number) {
		this._x.target += value;
	}
	public setHardX(value: number) {
		this._x.value = value;
		this._x.target = value;
	}
	public get targetX(): number {
		return this._x.target;
	}
	public set targetX(value: number) {
		this._x.target = value;
	}

	public get y(): number {
		return this._y.value;
	}
	public set y(value: number) {
		this._y.value = value;
	}
	public setHardY(value: number) {
		this._y.value = value;
		this._y.target = value;
	}
	public get targetY(): number {
		return this._y.target;
	}
	public set targetY(value: number) {
		this._y.target = value;
	}
	public increaseTargetY(value: number) {
		this._y.target += value;
	}
	public setTargetY(value: number) {
		this._y.target = value;
	}

	public get alpha(): number {
		return this._alpha.value;
	}
	public set alpha(value: number) {
		this._alpha.value = value;
	}
	public get targetAlpha(): number {
		return this._alpha.target;
	}
	public set targetAlpha(value: number) {
		this._alpha.target = value;
	}

	public get scale(): number {
		return this._scale.value;
	}
	public set scale(value: number) {
		this._scale.value = value;
	}
	public get targetScale(): number {
		return this._scale.target;
	}
	public set targetScale(value: number) {
		this._scale.target = value;
	}

	public get rotation(): number {
		return this._rotation.value;
	}
	public set rotation(value: number) {
		this._rotation.value = value;
	}
	public get targetRotation(): number {
		return this._rotation.target;
	}
	public set targetRotation(value: number) {
		this._rotation.value = value;
	}

	public setState(config: particles.ParticleLookupProps): void {
		//Set the values or defaults:
		let position: geometry.Vector2 = {
			x: config.x || 0,
			y: config.y || 0
		};

		let scale: number = typeof config.scale === 'undefined' ? 1 : config.scale;
		let alpha: number = typeof config.alpha === 'undefined' ? 1 : config.alpha;

		//Set on the animated props object:
		this.x = position.x;
		this.y = position.y;
		this.scale = scale;
		this.alpha = alpha;
	}

	/** Store the easing values */
	public easingToMemory(): void {
		this._easeMemory = {
			x: this._x.ease,
			y: this._y.ease,
			scale: this._scale.ease,
			alpha: this._alpha.ease,
			rotation: this._rotation.ease
		};
	}

	/** Restore the easing values */
	public easingFromMemory(): void {
		this.setEase(this._easeMemory);
	}

	//Switch the easing on the
	public setEase(easeConfig: particles.ParticleEaseConfig, lerp: boolean = false, frames: number = 0): void {
		if (!easeConfig) {
			easeConfig = {};
		}

		if (lerp) {
			if (easeConfig.x) this._x.lerpEase(easeConfig.x, frames);
			if (easeConfig.y) this._y.lerpEase(easeConfig.y, frames);
			if (easeConfig.rotation) this._rotation.lerpEase(easeConfig.rotation, frames);
			if (easeConfig.scale) this._scale.lerpEase(easeConfig.scale, frames);
			if (easeConfig.alpha) this._alpha.lerpEase(easeConfig.rotation, frames);
			return;
		}

		if (easeConfig.x) this._x.setEase(easeConfig.x);
		if (easeConfig.y) this._y.setEase(easeConfig.y);
		if (easeConfig.rotation) this._rotation.setEase(easeConfig.rotation);
		if (easeConfig.scale) this._scale.setEase(easeConfig.scale);
		if (easeConfig.alpha) this._alpha.setEase(easeConfig.alpha);
	}

	public tick(): void {
		//Update the inernia one tick:

		this._x.tick();
		this._y.tick();
		this._alpha.tick();
		this._scale.tick();

		//Set the sprites values:


		this._sprite.position.set(this._x.value, this._y.value);

		if (Math.abs(this._sprite.alpha - this._alpha.value) > 0.01) {
			this._sprite.alpha = this._alpha.value;
		}

		this._setScale();
	}

	public hardSetScale(scale: number, savedScale: number = undefined): void {
		this._scale.value = scale;
		this._scale.target = scale;
		this.targetScaleSaved = savedScale === undefined ? scale : savedScale;
	}

	public setVectorStyleMovement(vectorsOn: boolean, xConfig: any = null, yConfig: any = null) {
		if (xConfig)
			this._x.setVectorConfig(vectorsOn, xConfig.increaseRate || 0.3, xConfig.maxForce || 3, xConfig.dampen || 0.87);
		else this._x.setVectorConfig(vectorsOn);

		if (yConfig)
			this._y.setVectorConfig(vectorsOn, yConfig.increaseRate || 0.3, yConfig.maxForce || 3, yConfig.dampen || 0.87);
		else this._y.setVectorConfig(vectorsOn);
	}

	public setVectorFast() {
		this._x.setVectorFast();
		this._y.setVectorFast();
	}

	public setVectorStyleScaling(vectorsOn: boolean, config: any = null) {
		this._scale.setVectorConfig(vectorsOn);
	}

	public startBreathing(
		baseScale: number,
		changePerTick: number,
		maxChange: number,
		directions: Array<number>,
		startChangeValue: number = 0,
		startDirection: number = 1
	): void {
		//0 will randomise direction on start
		if (startDirection == 0) {
			startDirection = Math.random() < 0.5 ? -1 : 1;
		}

		this._breathing.addValue = startChangeValue * startDirection;
		this._breathing.direction = startDirection;
		this._breathing.active = true;
		this._breathing.config = {
			baseScale: baseScale,
			changePerTick: changePerTick,
			maxChange: maxChange,
			directions: directions
		};
	}

	public stopBreathing(): void {
		this._breathing.active = false;
	}

	private _processBreathing() {
		this._breathing.addValue +=
			this._breathing.direction * (this._breathing.config.changePerTick * (window as any).deltaTime);

		if (this._breathing.addValue > this._breathing.config.maxChange) {
			this._breathing.direction = -1;
		} else if (this._breathing.addValue < -1 * this._breathing.config.maxChange) {
			this._breathing.direction = 1;
		}

		this.targetScale = this.targetScaleSaved = this._breathing.config.baseScale + this._breathing.addValue;
	}

	private _setScale() {
		if (this._breathing.active) {
			this._processBreathing();
		}

		let scaleFactor = this._scale.value * globals.scaleFactorM;

		//Only scale if
		if (Math.abs(scaleFactor - this._sprite.scale.x) > 0.01) {
			this._sprite.scale.set(scaleFactor, scaleFactor);
		}
	}
}
